import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import {
  BarChart3,
  FileText,
  Filter,
  Shuffle,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Bell,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import clsx from 'clsx'
import NotificationBell from '@/components/NotificationBell'
import PushPermissionBanner from '@/components/PushPermissionBanner'

const navItems = [
  { path: '/', label: 'Dashboard', icon: BarChart3 },
  { path: '/reports', label: 'Reportes', icon: FileText },
  { path: '/funnel', label: 'Funnel', icon: Filter },
  { path: '/random', label: 'Random', icon: Shuffle },
  { path: '/notifications', label: 'Notificaciones', icon: Bell },
]

export default function Layout() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
      if (window.innerWidth >= 768) {
        setSidebarOpen(false)
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-40 safe-area-pt">
        <div className="flex items-center justify-between h-full px-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Xafra Ads" className="w-6 h-6" />
            <span className="font-semibold text-gray-900 dark:text-white">Xafra BI</span>
          </div>
          
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="p-2 -mr-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <User className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile sidebar */}
      <aside
        className={clsx(
          'md:hidden fixed top-14 left-0 bottom-16 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-30 transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-2">
            Navegación
          </p>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  )
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>
      
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-64 flex-col z-30 overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/background_mobile.png)' }}
        />
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95 dark:from-gray-800/95 dark:via-gray-800/90 dark:to-gray-800/95" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col h-full border-r border-gray-200/50 dark:border-gray-700/50">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center p-1 shadow-md">
              <img src="/logo.svg" alt="Xafra Ads" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">Xafra Ads</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">BI Dashboard</p>
            </div>
            <div className="ml-auto">
              <NotificationBell />
            </div>
          </div>
        
        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 backdrop-blur-sm',
                  isActive
                    ? 'bg-gradient-to-r from-orange-500/90 to-orange-600/90 text-white font-medium shadow-lg shadow-orange-500/30'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/60'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        
        {/* User section */}
        <div className="p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/70 dark:hover:bg-gray-700/70 transition-colors"
            >
              <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.userName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.customerName}
                </p>
              </div>
              <ChevronDown className={clsx(
                'w-4 h-4 text-gray-400 transition-transform',
                userMenuOpen && 'rotate-180'
              )} />
            </button>
            
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar Sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </aside>
      
      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40 safe-area-pb">
        <div className="flex">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                clsx(
                  'flex-1 flex flex-col items-center justify-center py-2',
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] mt-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      
      {/* Mobile user menu overlay */}
      {userMenuOpen && isMobile && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setUserMenuOpen(false)}
          />
          <div className="fixed inset-x-4 top-16 bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {user?.userName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user?.customerName}
                  </p>
                  {user?.isAdmin && (
                    <span className="badge-primary text-xs mt-1">Admin</span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
            >
              <LogOut className="w-5 h-5" />
              Cerrar Sesión
            </button>
          </div>
        </>
      )}
      
      {/* Main content */}
      <main className={clsx(
        'min-h-screen transition-all duration-300',
        'md:ml-64', // Desktop sidebar offset
        'pt-14 pb-16 md:pt-0 md:pb-0' // Mobile header and footer offset
      )}>
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>

      {/* Global push permission banner (portal to body, outside the sidebars) */}
      <PushPermissionBanner />
    </div>
  )
}
